import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import confetti from "canvas-confetti";
import { Sparkles, Loader2 } from "lucide-react";
import AnswerRecap from "@/components/storyboard/AnswerRecap";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import InputPrivacyNotice, {
  NO_PERSONAL_DATA_HINT,
} from "@/components/storyboard/InputPrivacyNotice";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import { GradedFeedback } from "@/components/storyboard/GradedFeedback";
import { QuizAttemptHint } from "@/components/storyboard/quiz/QuizAttemptHint";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import QuizFeedback from "@/components/storyboard/quiz/QuizFeedback";
import {
  canRetry,
  NO_ATTEMPTS_LEFT_FEEDBACK,
  retryLabel,
  ValidationAttemptsExceededError,
} from "@/lib/validationSubmission";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { WorksheetData, WorksheetTaskData } from "@/types/storyboard";
import { withTaskKeys } from "@/lib/worksheetStatus";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import type {
  WorksheetAnswerRecord,
  WorksheetTaskAnswer,
} from "@/lib/worksheetAnswers";

const HINT_TEXT =
  "Deine Antwort wird mit der Musterlösung verglichen und individuell ausgewertet. Du erhältst Hinweise, welche Aspekte du noch ergänzen könntest.";

const RETRY_HINT =
  "Nutze das Feedback und überarbeite deine Antwort – sie bleibt erhalten.";

export interface WorksheetAnswerState {
  value: string;
  submitted: boolean;
  correct: boolean;
  /** Normalized four-level grade key from the LLM validation, when available. */
  grade?: string;
  /** LLM feedback (Markdown) shown after submission. */
  feedback: string;
  /** Server-counted submissions for this task; absent until the first verdict. */
  attemptCount?: number;
  /** Attempt cap of this quiz type, null when it allows unlimited retries. */
  maxAttempts?: number | null;
}

type Phase = "task" | "bonus-offer";

export interface WorksheetRunnerState {
  phase: Phase;
  /** Which task list the currentIndex points into. */
  list: "main" | "bonus";
  currentIndex: number;
  /** Keyed by task id (main and additional tasks). */
  answers: Record<string, WorksheetAnswerState>;
  /** Whether the learner accepted the additional tasks. */
  doBonus: boolean;
}

export interface WorksheetRunnerProps {
  data: WorksheetData;
  /**
   * Validates one task via the LLM. `taskIndex` is the position in the
   * flattened main + additional task list; the backend resolves the matching
   * task (and its reference answer) from that index.
   */
  onValidate: (answer: string, taskIndex: number) => Promise<ValidationResult>;
  // Fired once the learner finishes, with the main-task score and the
  // answers kept for the read-only recap.
  onComplete?: (
    score: InteractiveElementScore,
    record: WorksheetAnswerRecord
  ) => void;
  /** Hydration when resuming from "angefangen". */
  initialPartial?: WorksheetRunnerState;
  /** Emits the latest in-progress state for "angefangen" persistence on close. */
  onPartialChange?: (partial: WorksheetRunnerState, index: number) => void;
  /** Read-only recap of a completed worksheet. */
  readOnly?: boolean;
  /** Hydration for the read-only recap. */
  reviewRecord?: WorksheetAnswerRecord;
}

const emptyState = (): WorksheetRunnerState => ({
  phase: "task",
  list: "main",
  currentIndex: 0,
  answers: {},
  doBonus: false,
});

const WorksheetRunner = ({
  data,
  onValidate,
  onComplete,
  initialPartial,
  onPartialChange,
  readOnly,
  reviewRecord,
}: WorksheetRunnerProps) => {
  const mainTasks = withTaskKeys(data.tasks ?? [], "main");
  const bonusTasks = withTaskKeys(data.additionalTasks ?? [], "bonus");
  const hasBonus = bonusTasks.length > 0;

  const [state, setState] = useState<WorksheetRunnerState>(
    () => initialPartial ?? emptyState()
  );

  // Gate onPartialChange so opening + immediately closing does not persist an
  // empty "angefangen" snapshot. Flipped on the first real interaction.
  const hasInteractedRef = useRef<boolean>(initialPartial != null);
  // Ensures completion side effects fire exactly once.
  const completedRef = useRef<boolean>(false);
  // The LLM validation is async/slow — track it for the loading + error state.
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (readOnly || !hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange, readOnly]);

  // ---- Read-only recap --------------------------------------------------
  if (readOnly) {
    return (
      <WorksheetReview
        mainTasks={mainTasks}
        bonusTasks={bonusTasks}
        record={reviewRecord ?? { answers: {}, doBonus: false }}
      />
    );
  }

  // ---- Interactive flow -------------------------------------------------
  const activeTasks = state.list === "main" ? mainTasks : bonusTasks;
  const currentTask = activeTasks[state.currentIndex];
  const currentAnswer = currentTask ? state.answers[currentTask.id] : undefined;

  const finishWorksheet = (answers: Record<string, WorksheetAnswerState>) => {
    if (completedRef.current) return;
    completedRef.current = true;
    const mainCorrect = mainTasks.filter((t) => answers[t.id]?.correct).length;
    onComplete?.(
      { correct: mainCorrect, total: mainTasks.length },
      { answers, doBonus: state.doBonus }
    );
  };

  const handleChange = (taskId: string, value: string) => {
    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [taskId]: {
          value,
          submitted: prev.answers[taskId]?.submitted ?? false,
          correct: prev.answers[taskId]?.correct ?? false,
          grade: prev.answers[taskId]?.grade,
          feedback: prev.answers[taskId]?.feedback ?? "",
        },
      },
    }));
  };

  const handleSubmit = async () => {
    if (!currentTask) return;
    const value = state.answers[currentTask.id]?.value ?? "";
    if (!value.trim()) return;

    // Position in the flattened main + bonus list — the backend resolves the
    // task (and its reference answer) from this index.
    const taskIndex =
      state.list === "main"
        ? state.currentIndex
        : mainTasks.length + state.currentIndex;

    setError(null);
    setIsValidating(true);
    try {
      const result = await onValidate(value, taskIndex);
      // correct == null means the LLM produced no verdict (e.g. its response
      // could not be parsed). Throw so the catch below shows the retry prompt,
      // rather than silently grading the answer as wrong.
      if (result.correct == null)
        throw new Error("validation returned no verdict");
      const correct = result.correct;
      hasInteractedRef.current = true;
      setState((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [currentTask.id]: {
            value,
            submitted: true,
            correct,
            grade: result.grade,
            feedback: result.feedback,
            attemptCount: result.attemptCount,
            maxAttempts: result.maxAttempts,
          },
        },
      }));
      if (correct) {
        confetti({ particleCount: 120, spread: 75, origin: { y: 0.6 } });
      }
    } catch (err) {
      // The server is the authority on the budget: if it says the attempts are
      // gone, the answer stands however many tries the UI believed it had left.
      if (err instanceof ValidationAttemptsExceededError) {
        hasInteractedRef.current = true;
        setState((prev) => ({
          ...prev,
          answers: {
            ...prev.answers,
            [currentTask.id]: {
              ...prev.answers[currentTask.id],
              value,
              submitted: true,
              correct: prev.answers[currentTask.id]?.correct ?? false,
              feedback: NO_ATTEMPTS_LEFT_FEEDBACK,
              attemptCount: err.attemptCount,
              maxAttempts: err.maxAttempts,
            },
          },
        }));
        return;
      }
      setError(
        "Die Auswertung hat nicht geklappt. Bitte versuche es gleich noch einmal."
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleRetry = () => {
    if (!currentTask) return;
    // The answer stays put so the learner can build on it rather than retype.
    setState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [currentTask.id]: {
          ...prev.answers[currentTask.id],
          submitted: false,
          correct: false,
          grade: undefined,
          feedback: "",
        },
      },
    }));
    setError(null);
  };

  const handleNext = () => {
    const isLast = state.currentIndex === activeTasks.length - 1;
    if (!isLast) {
      setState((prev) => ({ ...prev, currentIndex: prev.currentIndex + 1 }));
      return;
    }
    // End of the current list.
    if (state.list === "main" && hasBonus && !state.doBonus) {
      setState((prev) => ({ ...prev, phase: "bonus-offer" }));
      return;
    }
    finishWorksheet(state.answers);
  };

  const acceptBonus = () => {
    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      phase: "task",
      list: "bonus",
      currentIndex: 0,
      doBonus: true,
    }));
  };

  return (
    <div className="flex min-h-full flex-col gap-6">
      <IntroBlock data={data} />

      {state.phase === "task" && currentTask && (
        <TaskStep
          task={currentTask}
          index={state.currentIndex}
          total={activeTasks.length}
          isBonus={state.list === "bonus"}
          answer={currentAnswer}
          onChange={(val) => handleChange(currentTask.id, val)}
          onSubmit={handleSubmit}
          onRetry={handleRetry}
          onNext={handleNext}
          isLastInList={state.currentIndex === activeTasks.length - 1}
          willOfferBonus={state.list === "main" && hasBonus && !state.doBonus}
          isValidating={isValidating}
          error={error}
        />
      )}

      {state.phase === "bonus-offer" && (
        <BonusOffer
          mainTasks={mainTasks}
          answers={state.answers}
          bonusCount={bonusTasks.length}
          onAccept={acceptBonus}
          onDecline={() => finishWorksheet(state.answers)}
        />
      )}
    </div>
  );
};

export default WorksheetRunner;

// ---------------------------------------------------------------------------

const IntroBlock = ({ data }: { data: WorksheetData }) => (
  <div className="space-y-4">
    <div className="prose max-w-full text-primary prose-p:my-2">
      <CourseMarkdown>{data.introduction}</CourseMarkdown>
    </div>
    {data.scenario && (
      <div className="border-l-4 border-primary bg-accent rounded-r-xl px-5 py-4 space-y-2">
        <p className="font-bold text-primary uppercase tracking-widest">
          Szenario
        </p>
        <div className="prose max-w-full prose-p:my-2 text-primary">
          <CourseMarkdown>{data.scenario}</CourseMarkdown>
        </div>
      </div>
    )}
  </div>
);

interface TaskStepProps {
  task: WorksheetTaskData;
  index: number;
  total: number;
  isBonus: boolean;
  answer?: WorksheetAnswerState;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onRetry: () => void;
  onNext: () => void;
  isLastInList: boolean;
  willOfferBonus: boolean;
  isValidating: boolean;
  error: string | null;
}

const TaskStep = ({
  task,
  index,
  total,
  isBonus,
  answer,
  onChange,
  onSubmit,
  onRetry,
  onNext,
  isLastInList,
  willOfferBonus,
  isValidating,
  error,
}: TaskStepProps) => {
  const privacyNoticeId = useId();
  const submitted = answer?.submitted ?? false;
  const isCorrect = submitted ? (answer?.correct ?? false) : null;
  const value = answer?.value ?? "";

  const budget = {
    attemptCount: answer?.attemptCount,
    maxAttempts: answer?.maxAttempts,
  };
  // A wrong answer keeps the task open while the server still grants attempts.
  // Types without a cap report no budget and stay single-shot.
  const retryable = submitted && canRetry({ ...budget, correct: isCorrect });

  const nextLabel =
    !isLastInList || willOfferBonus ? "Nächste Aufgabe" : "Weiter";

  const renderAnswerField = () => {
    if (submitted) {
      return <AnswerRecap value={value} />;
    }

    return (
      <>
        {/* sr-only: same privacy copy as the placeholder, linked via
            aria-describedby so screen readers keep the warning after typing. */}
        <InputPrivacyNotice id={privacyNoticeId} className="sr-only" />
        <Textarea
          rows={6}
          value={value}
          placeholder={NO_PERSONAL_DATA_HINT}
          onChange={(e) => onChange(e.target.value)}
          disabled={isValidating}
          aria-describedby={privacyNoticeId}
          className="w-full min-h-40 rounded-lg border-2 border-primary bg-background shadow-sm resize-none transition-colors focus-visible:border-primary focus-visible:ring-primary/40"
        />
      </>
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      {/* Header: counter nested in h4 so it shares the heading’s computed
          size — `.interactive-element` flattens h4 to body, while `text-h4`
          would stay at the token size and look mismatched. */}
      <h4 className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-semibold text-primary">
        <span>{task.title}</span>
        <span className="font-semibold text-primary/70 whitespace-nowrap">
          {isBonus ? "Zusatzaufgabe" : "Aufgabe"} {index + 1}/{total}
        </span>
      </h4>

      <div className="prose max-w-full prose-p:my-2 text-primary">
        <CourseMarkdown>{task.description}</CourseMarkdown>
      </div>

      {/* Hint */}
      {!submitted && <TaskInstructionHint>{HINT_TEXT}</TaskInstructionHint>}

      {/* Above the verdict: the learner should know another go is coming
          before reading what went wrong. */}
      {submitted && !isCorrect && (
        <QuizAttemptHint
          attemptCount={budget.attemptCount}
          maxAttempts={budget.maxAttempts}
          description={RETRY_HINT}
        />
      )}

      {/* Feedback after submission */}
      {submitted && (
        <GradedFeedback
          result={{
            correct: isCorrect,
            grade: answer?.grade,
            feedback: answer?.feedback ?? "",
          }}
        />
      )}

      {/* Answer: editable textarea before submit, neutral recap after
          — GradedFeedback owns the verdict color. */}
      {renderAnswerField()}

      {error && <p className="font-semibold text-error-foreground">{error}</p>}

      {/* Footer pinned to the bottom of the modal */}
      <div className="mx-auto mt-auto flex w-full justify-center pt-2">
        {/* eslint-disable-next-line no-nested-ternary */}
        {!submitted ? (
          <Button
            variant="default"
            size="xl"
            onClick={onSubmit}
            disabled={!value.trim() || isValidating}
            className={
              isValidating
                ? "opacity-80 animate-pulse cursor-wait"
                : "disabled:opacity-60"
            }
          >
            {isValidating ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin w-5 h-5 shrink-0" />
                <span>Wird ausgewertet…</span>
              </span>
            ) : (
              "Ergebnis überprüfen"
            )}
          </Button>
        ) : retryable ? (
          <Button variant="default" size="xl" onClick={onRetry}>
            {retryLabel()}
          </Button>
        ) : (
          <Button variant="default" size="xl" onClick={onNext}>
            {nextLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

interface BonusOfferProps {
  mainTasks: WorksheetTaskData[];
  answers: Record<string, WorksheetAnswerState>;
  bonusCount: number;
  onAccept: () => void;
  onDecline: () => void;
}

const BonusOffer = ({
  mainTasks,
  answers,
  bonusCount,
  onAccept,
  onDecline,
}: BonusOfferProps) => {
  const correct = mainTasks.filter((t) => answers[t.id]?.correct).length;
  const total = mainTasks.length;

  return (
    <>
      {/* Compact card; the CTA sits outside, pinned to the bottom of the modal. */}
      <div className="space-y-4 rounded-2xl border border-primary/20 bg-accent p-6 text-center">
        <div className="flex justify-center">
          <div className="flex size-12 items-center justify-center rounded-full border-2 border-primary bg-white">
            <Sparkles className="size-6 text-primary" />
          </div>
        </div>
        <p className="text-primary font-semibold">
          Du hast {correct} von {total} {total === 1 ? "Aufgabe" : "Aufgaben"}{" "}
          richtig gelöst.
        </p>
        <div className="flex justify-center">
          <span className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 font-semibold text-white">
            Möchtest du noch {bonusCount}{" "}
            {bonusCount === 1 ? "Zusatzaufgabe" : "Zusatzaufgaben"} bearbeiten?
          </span>
        </div>
      </div>
      <div className="mx-auto mt-auto w-4/5 space-y-3">
        <Button size="xl" className="w-full" onClick={onAccept}>
          Ja, Zusatzaufgaben bearbeiten
        </Button>
        <button
          type="button"
          onClick={onDecline}
          className="block w-full text-center font-semibold text-primary/70 underline-offset-4 hover:text-primary hover:underline"
        >
          Ohne Zusatzaufgaben beenden
        </button>
      </div>
    </>
  );
};

interface WorksheetReviewProps {
  mainTasks: WorksheetTaskData[];
  bonusTasks: WorksheetTaskData[];
  record: WorksheetAnswerRecord;
}

const renderReviewScore = (correct: number, total: number) => {
  if (total === 0) return null;

  return (
    <span className="inline-flex items-center rounded-full border border-info-border bg-info-light px-3 py-1 text-info-foreground">
      {correct}/{total} Aufgaben richtig
    </span>
  );
};

/**
 * Completed-card recap — same header chrome as the inline quiz review.
 */
const WorksheetReview = ({
  mainTasks,
  bonusTasks,
  record,
}: WorksheetReviewProps) => {
  const reviewedTasks = [...mainTasks, ...(record.doBonus ? bonusTasks : [])];
  const correct = reviewedTasks.filter(
    (task) => record.answers[task.id]?.correct
  ).length;

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 pl-3 text-base font-semibold">
        <p className="text-primary">Deine Antworten</p>
        {renderReviewScore(correct, reviewedTasks.length)}
      </div>
      {reviewedTasks.map((task, index) => (
        <ReviewTaskCard
          key={task.id}
          task={task}
          index={index}
          total={reviewedTasks.length}
          isBonus={index >= mainTasks.length}
          answer={record.answers[task.id]}
        />
      ))}
    </div>
  );
};

interface ReviewTaskCardProps {
  task: WorksheetTaskData;
  index: number;
  total: number;
  isBonus: boolean;
  answer?: WorksheetTaskAnswer;
}

const renderReviewFeedback = (
  answer: WorksheetTaskAnswer | undefined,
  isCorrect: boolean | null
) => {
  if (answer == null || isCorrect === null) return null;

  const feedback = answer.feedback ?? "";
  return (
    <QuizFeedback
      isSubmitted
      isCorrect={isCorrect}
      positiveFeedback={isCorrect ? feedback : undefined}
      negativeFeedback={isCorrect ? undefined : feedback}
      className="mb-0 w-full"
    />
  );
};

const ReviewTaskCard = ({
  task,
  index,
  total,
  isBonus,
  answer,
}: ReviewTaskCardProps): ReactNode => {
  // In the review a persisted answer always represents a submitted task.
  const submitted = answer != null;
  const isCorrect = submitted ? answer.correct : null;
  const counterLabel = isBonus ? "Zusatzaufgabe" : "Aufgabe";

  return (
    <div
      data-slot="quiz-review-question"
      className="space-y-4 rounded-2xl border border-border bg-card py-5 pl-3 pr-5"
    >
      <QuizEntryCounter currentIndex={index} total={total} label={counterLabel}>
        <div className="prose min-w-0 max-w-full font-semibold text-primary">
          <p>{task.title}</p>
        </div>
      </QuizEntryCounter>

      <div className="prose max-w-full prose-p:my-2 text-primary">
        <CourseMarkdown>{task.description}</CourseMarkdown>
      </div>

      <AnswerRecap value={answer?.value ?? ""} />

      {renderReviewFeedback(answer, isCorrect)}
    </div>
  );
};
